import { useEffect, useState } from 'react';
import { api } from '../../lib/api.js';
import { Loading, Empty, Stat, BarChart, PageHeader } from '../../components/ui.jsx';
import Icon from '../../components/Icon.jsx';
import { currency, formatDate } from '../../lib/format.js';

export default function Earnings() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get('/api/nurses/me/earnings').then(setData).catch(() => setData(null));
  }, []);

  if (!data) return <Loading rows={2} />;

  const chart = data.monthly.map((m) => ({
    label: new Date(`${m.month}-01T12:00:00`).toLocaleDateString('en-IN', { month: 'short' }),
    value: m.total,
  }));

  return (
    <>
      <PageHeader title="Earnings" subtitle="Everything you have earned through HealAtHome." />

      <div className="grid grid-3 mb-3">
        <Stat
          label="Total earned"
          value={currency(data.earned)}
          sub={`${data.completed_jobs} completed visits`}
          icon="wallet"
        />
        <Stat label="Confirmed upcoming" value={currency(data.upcoming)} sub="Cash due at those visits" icon="calendar" />
        <Stat
          label="Average per visit"
          value={currency(data.completed_jobs ? data.earned / data.completed_jobs : 0)}
          sub="Across all completed visits"
          icon="chart"
        />
      </div>

      <div className="detail-layout">
        <section className="card">
          <div className="card-header">
            <h2 className="grow">Recent completed visits</h2>
            {data.recent.length > 0 && <span className="small dim">{data.recent.length} shown</span>}
          </div>
          {data.recent.length === 0 ? (
            <div className="card-body">
              <Empty art="earnings" title="No earnings yet">
                Once you complete your first visit it will be listed here with the cash you collected.
              </Empty>
            </div>
          ) : (
            <div className="table-wrap">
              <table className="data">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Patient</th>
                    <th>Service</th>
                    <th className="td-right">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((r) => (
                    <tr key={r.id}>
                      <td className="nowrap">{formatDate(r.date)}</td>
                      <td>{r.patient_name}</td>
                      <td>{r.service_name}</td>
                      <td className="bold nowrap td-right">{currency(r.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <aside className="stack sticky-aside">
          <section className="card">
            <div className="card-header">
              <span className="icon-tile icon-tile-neutral" style={{ width: 34, height: 34 }}>
                <Icon name="chart" size={16} />
              </span>
              <h3>Monthly earnings</h3>
            </div>
            <div className="card-body">
              <BarChart data={chart} format={(v) => `₹${Math.round(v / 100) / 10}k`} />
            </div>
          </section>

          <div className="banner banner-neutral" style={{ marginBottom: 0 }}>
            <Icon name="info" />
            <span className="grow tiny">
              Every visit is paid in cash by the patient when you finish. HealAtHome does not hold or transfer any
              money — nothing is deducted from these amounts.
            </span>
          </div>
        </aside>
      </div>
    </>
  );
}
